import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUp, Info, Eye, Sparkles, EyeOff } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";
import { scoreColor } from "../lib/growth";
import CompetitorGap from "../components/growth/CompetitorGap";
import LocalPosition from "../components/growth/LocalPosition";

const CONF = {
  observed: { label: "Observed", bg: "rgba(147,192,32,.12)", color: "#5F8213", Icon: Eye },
  estimated: { label: "Estimated", bg: "rgba(229,169,61,.16)", color: "#A9781F", Icon: Sparkles },
  unavailable: { label: "Not available", bg: "#F1F2EE", color: "#9A9C90", Icon: EyeOff },
};

function ConfidenceTag({ kind }) {
  const c = CONF[kind] || CONF.observed;
  const Icon = c.Icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: c.bg, color: c.color }} data-testid={`confidence-${kind}`}>
      <Icon size={10} strokeWidth={2.5} />{c.label}
    </span>
  );
}

export default function Competitors() {
  const { branchId } = useBranch();
  const [filter, setFilter] = useState("overall");
  const { data: cp } = useApi(`/growth/competitor-pillars?branch_id=${branchId}`, [branchId]);
  const { data: li } = useApi(`/growth/local-intelligence?branch_id=${branchId}`, [branchId]);
  const { data } = useApi(`/competitors?branch_id=${branchId}&filter=${filter}`, [branchId, filter]);

  return (
    <>
      <TopBar />
      <div className="p-6 sm:p-8 max-w-[1400px]">
        <div className="mb-6 animate-rise">
          <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Competitors</h1>
          <p className="text-ink-2 mt-1">Your position in the local market — on everything that's publicly visible.</p>
        </div>
        {/* Hero position */}
        <div className="card p-8 animate-rise" data-testid="local-position">
          <span className="label-caps">Your local position</span>
          <div className="flex items-end gap-4 mt-2 flex-wrap">
            <span className="text-6xl font-extrabold tracking-tighter text-ink">#{cp?.position ?? "–"}</span>
            <span className="text-2xl font-semibold text-ink-3 mb-1.5">of {cp?.field_size ?? 12}</span>
            <span className="inline-flex items-center gap-1 mb-2.5 text-sm font-bold" style={{ color: "#678A16" }}>
              <ArrowUp size={15} strokeWidth={2.5} /> 2 positions this month
            </span>
          </div>
          <p className="text-sm text-ink-2 mt-3 max-w-2xl inline-flex items-start gap-2">
            <Info size={15} strokeWidth={1.75} className="mt-0.5 shrink-0" style={{ color: "#9A9C90" }} />
            For your branch we see the full picture. For competitors we compare on what's <span className="font-semibold text-ink">publicly observable</span> — private social insights (reach, saves, shares) are shown as “Not available”, never guessed.
          </p>
        </div>

        {/* Local category ranks + competitor gap */}
        {li && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LocalPosition local={li.local} />
            <div className="lg:hidden" />
          </div>
        )}
        {li && <div className="mt-6"><CompetitorGap gap={li.competitor_gap} /></div>}

        {/* Pillar comparison table */}
        {cp && (
          <div className="card mt-6 overflow-hidden" data-testid="pillar-comparison">
            <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: "#EDEEE9" }}>
              <span className="label-caps">How you compare · by pillar</span>
              <div className="flex items-center gap-2"><ConfidenceTag kind="observed" /><ConfidenceTag kind="estimated" /><ConfidenceTag kind="unavailable" /></div>
            </div>
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] px-5 py-2.5 text-[11px] font-bold uppercase tracking-wide text-ink-3 border-b" style={{ borderColor: "#EDEEE9" }}>
              <span>Factor</span><span className="text-right">You</span><span className="text-right">Local avg</span><span className="text-right">Leader</span>
            </div>
            {cp.rows.map((r, i) => {
              const win = r.you >= r.leader ? "#5F8213" : r.you >= r.local_average ? "#16180F" : "#D0526B";
              return (
                <motion.div key={r.key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center px-5 py-3 border-b last:border-0" style={{ borderColor: "#EDEEE9" }} data-testid={`compare-${r.key}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-ink truncate">{r.label}</span>
                    <ConfidenceTag kind={r.confidence} />
                  </span>
                  <span className="text-right font-extrabold tabular-nums" style={{ color: win }}>{r.you}</span>
                  <span className="text-right tabular-nums text-ink-2">{r.local_average}</span>
                  <span className="text-right tabular-nums text-ink-2">{r.leader}</span>
                </motion.div>
              );
            })}
            {cp.social_observability && (
              <div className="px-5 py-4 bg-[#F7F8F2] text-xs text-ink-2">
                <span className="font-semibold text-ink">On social, for competitors we can see:</span>{" "}
                {cp.social_observability.observed.join(", ")}.{" "}
                <span className="font-semibold text-ink">Estimated:</span> {cp.social_observability.estimated.join(", ")}.{" "}
                <span className="font-semibold" style={{ color: "#9A9C90" }}>Not available:</span> {cp.social_observability.not_available.join(", ")}.
              </div>
            )}
          </div>
        )}

        {/* Competitor cards — observable public stats */}
        {cp?.competitors && (
          <div className="mt-8">
            <h2 className="text-xl font-bold tracking-tight text-ink mb-4">Local agencies</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[...cp.competitors].sort((a, b) => b.overall - a.overall).map((c) => (
                <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="card p-5" data-testid={`competitor-${c.id}`}
                  style={c.is_you ? { boxShadow: "0 0 0 1.5px #93C020 inset" } : {}}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-ink flex items-center gap-2">
                      {c.name}
                      {c.is_you && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "rgba(147,192,32,.15)", color: "#678A16" }}>You</span>}
                    </span>
                    <span className="text-2xl font-extrabold tracking-tighter" style={{ color: scoreColor(c.overall) }}>{c.overall}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mt-4">
                    <Stat label="Google rating" value={`${c.google_rating} ★`} />
                    <Stat label="Reviews (30d)" value={`${c.reviews} · +${c.reviews_30d}`} />
                    <Stat label="Followers" value={c.followers.toLocaleString()} />
                    <Stat label="Posting / wk" value={c.posting_freq} />
                    <Stat label="Video %" value={`${c.video_pct}%`} />
                    <Stat label="Engagement" value={`${c.public_engagement}%`} estimated />
                    <Stat label="Website" value={c.website_score} />
                    <Stat label="Reach / saves" value="Not available" unavailable />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Existing social share-of-voice / frequency (all public / estimated) */}
        <div className="flex flex-wrap gap-2 mt-8">
          {[{ key: "overall", label: "Overall" }, { key: "instagram", label: "Instagram" }, { key: "facebook", label: "Facebook" }, { key: "linkedin", label: "LinkedIn" }, { key: "engagement", label: "Engagement" }, { key: "consistency", label: "Posting consistency" }].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className="rounded-full px-3.5 py-1.5 text-sm font-medium transition-all border"
              style={filter === f.key ? { background: "#151515", color: "#fff", borderColor: "#151515" } : { background: "#fff", color: "#6B6B6B", borderColor: "#E7E8E4" }}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 card p-2">
            <div className="px-4 py-3"><span className="label-caps">Social leaderboard</span></div>
            <div className="divide-y" style={{ borderColor: "#E7E8E4" }}>
              {data?.leaderboard.map((c, i) => (
                <motion.div key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                  className="flex items-center gap-4 px-4 py-3" data-testid={`comp-row-${c.id}`}
                  style={c.is_you ? { background: "rgba(147,192,32,.06)", borderRadius: 10 } : {}}>
                  <span className="w-6 text-center font-bold text-ink-3 tabular-nums">{c.display_rank}</span>
                  <span className={`flex-1 truncate ${c.is_you ? "font-bold text-ink" : "font-medium text-ink"}`}>{c.name}</span>
                  <span className="w-32 hidden sm:block">
                    <span className="block h-1.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
                      <span className="block h-full rounded-full" style={{ width: `${c.display_value}%`, background: c.is_you ? "#93C020" : "#C9CBC4" }} />
                    </span>
                  </span>
                  <span className="w-10 text-right font-bold text-ink tabular-nums">{c.display_value}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="card p-6">
              <span className="label-caps">Posting frequency</span>
              <MetricBar label="Your agency" value={data?.posting_frequency.you} suffix="/week" pct={(data?.posting_frequency.you || 0) / 7 * 100} highlight />
              <MetricBar label="Local average" value={data?.posting_frequency.local_average} suffix="/week" pct={(data?.posting_frequency.local_average || 0) / 7 * 100} />
              <MetricBar label="Top performers" value={data?.posting_frequency.top} suffix="/week" pct={(data?.posting_frequency.top || 0) / 7 * 100} />
            </div>
            <div className="card p-6">
              <div className="flex items-center gap-2"><span className="label-caps">Engagement</span><ConfidenceTag kind="estimated" /></div>
              <MetricBar label="Your agency" value={data?.engagement.you} suffix="%" pct={(data?.engagement.you || 0) * 15} highlight />
              <MetricBar label="Local average" value={data?.engagement.local_average} suffix="%" pct={(data?.engagement.local_average || 0) * 15} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, estimated, unavailable }) {
  return (
    <div>
      <div className="text-[11px] text-ink-3 flex items-center gap-1">{label}{estimated && <Sparkles size={9} style={{ color: "#A9781F" }} />}</div>
      <div className="text-sm font-bold tabular-nums" style={{ color: unavailable ? "#B4B6AD" : "#16180F", fontStyle: unavailable ? "italic" : "normal", fontWeight: unavailable ? 500 : 700 }}>{value}</div>
    </div>
  );
}

function MetricBar({ label, value, suffix, pct, highlight }) {
  return (
    <div className="mt-3.5 first:mt-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-ink-2">{label}</span>
        <span className="font-bold text-ink tabular-nums">{value}{suffix}</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, background: highlight ? "#93C020" : "#C9CBC4" }} />
      </div>
    </div>
  );
}
